import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { cloneDeep, forEach } from 'lodash';
import { Table as BootstrapTable } from 'react-bootstrap';
import Table from 'components/Layout/Table';
import RadioInput from 'components/form/RadioInput';
import { getDataFromLocalStorage } from 'utils/helpers';
import './Registration.scss';
import EventPricing from './EventPricing';

const ViewRegistration = ({
  isEdit,
  handelPriceSelection,
  isNational,
  addOnServicesData,
}) => {
  const [priceArray, setPriceArray] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [addOnPriceArray, setAddOnPriceArray] = useState([]);
  const userData = getDataFromLocalStorage();
  const { user_type, membership_plan_id } = userData;
  const isPremiumMember = [3, 12].includes(membership_plan_id);
  const isFreeMember = [2, 11].includes(membership_plan_id);
  const { eventData, inrPrice } = useSelector((state) => ({
    eventData: state.global.eventData,
    inrPrice: state.currency.inrPrice
  }));
  const reduxData = useSelector((state) => state.student);
  const { selectedData } = reduxData || {};
  const [selectedIds, setSelectedIds] = useState(
    selectedData?.length > 0 ? selectedData.map((item) => item.id) : []
  );

  const {
    event_pricing = [],
    add_on_pricing = [],
    dashboard_pricing_selection = [],
    pricing_categories = [],
    pricing_currency,
  } = eventData;

  const findCategory =
    user_type === '0'
      ? pricing_categories
      : dashboard_pricing_selection.find(
        (o) => `${o.id}` === `${membership_plan_id}`
      )?.data || [];

  // Helper function to convert price based on currency
  const convertPrice = (price) => {
    let convertedPrice = parseFloat(price);
    if (isNaN(convertedPrice)) return 0;

    if (pricing_currency === "USD" && isNational) {
      convertedPrice = convertedPrice * inrPrice;
    } else if (pricing_currency === "INR" && !isNational) {
      convertedPrice = convertedPrice / inrPrice;
    }

    return Number(convertedPrice.toFixed(2));
  };

  // Helper function to get currency symbol
  const getCurrencySymbol = () => {
    if (['0', '1', '6'].includes(user_type)) {
      return pricing_currency === 'INR' ? '₹' : '$';
    }
    return isNational ? '₹' : '$';
  };

  // Helper function to calculate radio button amount
  const calculateRadioAmount = (priceList, addOnList) => {
    let radioAmount = 0;

    forEach(priceList, (elm) => {
      const splitData = elm.split('-');
      if (splitData[0] === 'event_pricing') {
        const parentFind = event_pricing.find(
          (o) => `${o.id}` === splitData[1]
        )?.[splitData[2]];
        const childFind = parentFind?.find((o) => `${o.id}` === splitData[3])?.[
          splitData[4]
        ];
        const priceAmount = childFind ? convertPrice(childFind) : 0;
        radioAmount += priceAmount;
      }
    });

    forEach(addOnList, (elm) => {
      const splitData = elm.split('-');
      if (splitData[0] === 'add_on_pricing') {
        const res = add_on_pricing.find((o) => `${o.id}` === splitData[1])?.[
          splitData[2]
        ];
        radioAmount += convertPrice(res);
      }
    });

    return radioAmount;
  };

  // Helper function to calculate checkbox amount
  const calculateCheckboxAmount = (selectedIdsList) => {
    const selectedRows = (addOnServicesData?.services || []).filter((item) =>
      selectedIdsList.includes(item.id)
    );

    const totalPremium = selectedRows.reduce(
      (acc, curr) => acc + convertPrice(curr.premium_member),
      0
    );
    const totalFree = selectedRows.reduce(
      (acc, curr) => acc + convertPrice(curr.free_member),
      0
    );

    return isPremiumMember ? totalPremium : totalFree;
  };

  // Helper function to calculate and update total amount
  const calculateAndUpdateTotal = (priceList, addOnList, selectedIdsList) => {
    const radioAmount = calculateRadioAmount(priceList, addOnList);
    const checkboxAmount = calculateCheckboxAmount(selectedIdsList);
    const finalAmount = radioAmount + checkboxAmount;

    setTotalAmount(finalAmount);

    if (handelPriceSelection) {
      handelPriceSelection(finalAmount);
    }
  };

  const handelClick = (e) => {
    const elemID = e.target.id;
    let list = cloneDeep(priceArray);
    let addonlist = cloneDeep(addOnPriceArray);
    
    if (elemID.includes('event_pricing')) {
      if (list.includes(elemID)) {
        list = list.filter((o) => o !== elemID);
      } else {
        list = [elemID];
      }
      setPriceArray(list);
    } else {
      if (addonlist.includes(elemID)) {
        addonlist = addonlist.filter((o) => o !== elemID);
      } else {
        const idArray = elemID.split('-');
        addonlist = addonlist.filter(
          (o) =>
            o !==
            `${idArray[0]}-${idArray[1]}-${idArray[2] === 'non_premium_members'
              ? 'premium_members'
              : 'non_premium_members'
            }`
        );
        addonlist = [...addonlist, elemID];
      }
      setAddOnPriceArray(addonlist);
    }

    calculateAndUpdateTotal(list, addonlist, selectedIds);
  };

  const header = [
    {
      title: <div className="text-nowrap">Addons Section</div>,
    },
    {
      title: <div className="text-nowrap">Free</div>,
    },
    {
      title: <div className="text-nowrap">Premium</div>,
    },
  ];

  const handleCheckboxChange = (id, isChecked) => {
    setSelectedIds((prevSelected) => {
      const updatedSelection = isChecked
        ? [...prevSelected, id]
        : prevSelected.filter((item) => item !== id);

      calculateAndUpdateTotal(priceArray, addOnPriceArray, updatedSelection);

      return updatedSelection;
    });
  };

  const getTextClass = (isMember) =>
    `text-nowrap text-14-300-18 ${isMember ? 'color-c5c5' : 'color-black-olive'}`;

  const formatPrice = (price) => {
    const currencySymbol = getCurrencySymbol();
    const shouldConvert = !['0', '1', '6'].includes(user_type);
    const displayPrice = shouldConvert ? convertPrice(price) : price;
    return `${currencySymbol} ${displayPrice}`;
  };

  const rowData = (
    selectedData?.length > 0 ? selectedData : addOnServicesData?.services || []
  ).map(({ id, category, free_member, premium_member }) => {
    return {
      data: [
        {
          value: (
            <div className="d-flex cms-10">
              <input
                type="checkbox"
                className={`custom-checkbox ${selectedIds.includes(id) ? 'activeChecked' : 'activeUnchecked'}`}
                onChange={(e) => handleCheckboxChange(id, e.target.checked)}
                checked={selectedIds.includes(id)}
              />
              <span className="text-center w-100">{category}</span>
            </div>
          ),
        },
        {
          value: (
            <div className={getTextClass(isPremiumMember)}>
              {formatPrice(free_member)}
            </div>
          ),
        },
        {
          value: (
            <div className={getTextClass(isFreeMember)}>
              {formatPrice(premium_member)}
            </div>
          ),
        },
      ],
    };
  });

  return (
    <div
      id="common-registration-block"
      className="w-100 overflow-auto iferp-scroll"
    >
      <EventPricing
        isNational={isNational}
        priceArray={priceArray}
        isEdit={isEdit}
        handelClick={handelClick}
      />

      {rowData?.length > 0 && (
        <>
          <div className="text-16-500 color-title-navy font-poppins mb-3 cmt-5">
            Add-On Services
          </div>
          <Table
            header={header}
            rowData={rowData}
            customHeaderStyle={{
              backgroundColor: '#2148C0',
              color: 'white',
            }}
          />
        </>
      )}
      {add_on_pricing.length > 0 && (
        <>
          <div className="text-18-500 color-title-navy font-poppins mb-3 pt-3">
            Add On Pricing
          </div>
          <BootstrapTable bordered>
            <thead className="table-header-container">
              <tr className="table-header-row-container">
                <th className="header-cell">Add On Categories</th>
                <th className="header-cell">Premium Members</th>
                <th className="header-cell">Non-Premium Members</th>
              </tr>
            </thead>
            <tbody className="table-body-container">
              {add_on_pricing.map((elem, index) => {
                const { id, category, premium_members, non_premium_members } = elem;
                const premiumPrice = formatPrice(premium_members);
                const nonPremiumPrice = formatPrice(non_premium_members);

                return (
                  <tr className="table-body-row-container" key={index}>
                    <td className="text-16-400 color-black-olive row-cell text-start ps-5 pe-2">
                      {category}
                    </td>
                    <td className="text-16-500 color-black-olive row-cell text-start ps-5 pe-2">
                      {isEdit ? (
                        <RadioInput
                          id={`add_on_pricing-${id}-premium_members`}
                          checked={addOnPriceArray.includes(
                            `add_on_pricing-${id}-premium_members`
                          )}
                          label={premiumPrice}
                          className="pe-4"
                          value="1"
                          onChange={handelClick}
                        />
                      ) : (
                        premiumPrice
                      )}
                    </td>
                    <td className="text-16-500 color-black-olive row-cell text-start ps-5 pe-2">
                      {isEdit ? (
                        <RadioInput
                          id={`add_on_pricing-${id}-non_premium_members`}
                          checked={addOnPriceArray.includes(
                            `add_on_pricing-${id}-non_premium_members`
                          )}
                          label={nonPremiumPrice}
                          className="pe-4"
                          value="1"
                          onChange={handelClick}
                        />
                      ) : (
                        nonPremiumPrice
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </BootstrapTable>
        </>
      )}
    </div>
  );
};
export default ViewRegistration;



//OLD
// import React, { useEffect, useState } from 'react';
// import { useSelector } from 'react-redux';
// import { cloneDeep, forEach } from 'lodash';
// import { Table as BootstrapTable } from 'react-bootstrap';
// import Table from 'components/Layout/Table';
// import RadioInput from 'components/form/RadioInput';
// import { getDataFromLocalStorage } from 'utils/helpers';
// import './Registration.scss';
// import EventPricing from './EventPricing';

// const ViewRegistration = ({
//   isEdit,
//   handelPriceSelection,
//   isNational,
//   addOnServicesData,
// }) => {
//   const [priceArray, setPriceArray] = useState([]);
//   const [totalAmount, setTotalAmount] = useState(0);
//   const [addOnPriceArray, setAddOnPriceArray] = useState([]);
//   const userData = getDataFromLocalStorage();
//   const { user_type, membership_plan_id } = userData;
//   const isPremiumMember = [3, 12].includes(membership_plan_id);
//   const isFreeMember = [2, 11].includes(membership_plan_id);
//   const { eventData, inrPrice } = useSelector((state) => ({
//     eventData:
//       user_type === '0' ? state.global.eventData : state.global.eventData,
//     inrPrice: state.currency.inrPrice
//   }));
//   const reduxData = useSelector((state) => state.student);
//   const { selectedData } = reduxData || {};
//   const [selectedIds, setSelectedIds] = useState(
//     selectedData?.length > 0 ? selectedData.map((item) => item.id) : []
//   );

//   const {
//     event_pricing = [],
//     add_on_pricing = [],
//     dashboard_pricing_selection = [],
//     pricing_categories = [],
//     event_mode,
//     pricing_currency,
//   } = eventData;

//   const findCategory =
//     user_type === '0'
//       ? pricing_categories
//       : dashboard_pricing_selection.find(
//         (o) => `${o.id}` === `${membership_plan_id}`
//       )?.data || [];

//   const handelClick = (e) => {
//     const elemID = e.target.id;
//     let list = cloneDeep(priceArray);
//     let addonlist = cloneDeep(addOnPriceArray);
    
//     if (elemID.includes('event_pricing')) {
//       if (list.includes(elemID)) {
//         list = list.filter((o) => o !== elemID);
//       } else {
//         // list = [...list, elemID];
//         list = [elemID];
//       }
//       setPriceArray(list);
//     } else {
//       if (addonlist.includes(elemID)) {
//         addonlist = addonlist.filter((o) => o !== elemID);
//       } else {
//         const idArray = elemID.split('-');

//         addonlist = addonlist.filter(
//           (o) =>
//             o !==
//             `${idArray[0]}-${idArray[1]}-${idArray[2] === 'non_premium_members'
//               ? 'premium_members'
//               : 'non_premium_members'
//             }`
//         );
//         addonlist = [...addonlist, elemID];
//       }
//       setAddOnPriceArray(addonlist);
//     }
   
//     // Calculate the amount from radio buttons
//     const convertPrice = (price) => {
//       let convertedPrice = parseFloat(price);
//       if (isNaN(convertedPrice)) return 0;

//       if (pricing_currency === "USD" && isNational) {
//         // USD → INR
//         convertedPrice = convertedPrice * inrPrice;
//       } else if (pricing_currency === "INR" && !isNational) {
//         // INR → USD
//         convertedPrice = convertedPrice / inrPrice;
//       }

//       return Number(convertedPrice.toFixed(2));
//     };


//     let radioAmount = 0;
//     forEach(list, (elm) => {
//       const splitData = elm.split('-');
//       if (splitData[0] === 'event_pricing') {
//         const parentFind = event_pricing.find(
//           (o) => `${o.id}` === splitData[1]
//         )?.[splitData[2]];
//         let childFind = parentFind.find((o) => `${o.id}` === splitData[3])?.[
//           splitData[4]
//         ];
//         let priceAmount = childFind ? convertPrice(childFind) : 0;
//         radioAmount += priceAmount;
//       }
//     });

//     forEach(addonlist, (elm) => {
//       const splitData = elm.split('-');
//       if (splitData[0] === 'add_on_pricing') {
//         const res = add_on_pricing.find((o) => `${o.id}` === splitData[1])?.[
//           splitData[2]
//         ];
//         radioAmount += convertPrice(res);
//       }
//     });


//     // Calculate the amount from checkboxes
//     const selectedRows = (addOnServicesData?.services || []).filter((item) =>
//       selectedIds.includes(item.id)
//     );

    
//     const totalPremium = selectedRows.reduce(
//       (acc, curr) => acc + convertPrice(curr.premium_member),
//       0
//     );
//     const totalFree = selectedRows.reduce(
//       (acc, curr) => acc + convertPrice(curr.free_member),
//       0
//     );
//     const checkboxAmount = isPremiumMember ? totalPremium : totalFree;

//     // Combine radio and checkbox amounts
//     const finalAmount = radioAmount + checkboxAmount;

//     // Update the total amount state
//     setTotalAmount(finalAmount);

//     // Pass the final amount to handelPriceSelection
//     if (handelPriceSelection) {
//       handelPriceSelection(finalAmount);
//     }
//   };

  
//   const showEventPricing =
//     findCategory.some((o) => o.is_checked) || user_type === '0';

//   const header = [
//     {
//       title: <div className="text-nowrap">Addons Section</div>,
//     },
//     {
//       title: <div className="text-nowrap">Free</div>,
//     },
//     {
//       title: <div className="text-nowrap">Premium</div>,
//     },
//   ];
//   const handleCheckboxChange = (id, isChecked) => {
//     setSelectedIds((prevSelected) => {
//       const updatedSelection = isChecked
//         ? [...prevSelected, id]
//         : prevSelected.filter((item) => item !== id);

//       const selectedRows = (addOnServicesData?.services || []).filter((item) =>
//         updatedSelection.includes(item.id)
//       );

//             const convertPrice = (price) => {
//         let convertedPrice = parseFloat(price);
//         if (isNaN(convertedPrice)) return 0;

//         if (pricing_currency === "USD" && isNational) {
//           // USD → INR
//           convertedPrice = convertedPrice * inrPrice;
//         } else if (pricing_currency === "INR" && !isNational) {
//           // INR → USD
//           convertedPrice = convertedPrice / inrPrice;
//         }

//         return Number(convertedPrice.toFixed(2));
//       };

//       const totalPremium = selectedRows.reduce(
//         (acc, curr) => acc + convertPrice(curr.premium_member),
//         0
//       );
//       const totalFree = selectedRows.reduce(
//         (acc, curr) => acc + convertPrice(curr.free_member),
//         0
//       );
//       const checkboxAmount = isPremiumMember ? totalPremium : totalFree;




//       // Calculate the amount from radio buttons
//       let radioAmount = 0;
//       forEach(priceArray, (elm) => {
//         const splitData = elm.split('-');
//         if (splitData[0] === 'event_pricing') {
//           const parentFind = event_pricing.find(
//             (o) => `${o.id}` === splitData[1]
//           )?.[splitData[2]];
//           let childFind = parentFind.find((o) => `${o.id}` === splitData[3])?.[
//             splitData[4]
//           ];
//           let priceAmount = childFind ? convertPrice(childFind) : 0;
//           radioAmount += priceAmount;
//         }
//       });

//       forEach(addOnPriceArray, (elm) => {
//         const splitData = elm.split('-');
//         if (splitData[0] === 'add_on_pricing') {
//           const res = add_on_pricing.find((o) => `${o.id}` === splitData[1])?.[
//             splitData[2]
//           ];
//           radioAmount += convertPrice(res);
//         }
//       });

//       // Combine radio and checkbox amounts
//       const finalAmount = radioAmount + checkboxAmount;
//       // Update the total amount state
//       setTotalAmount(finalAmount);

//       // Pass the final amount to handelPriceSelection
//       if (handelPriceSelection) {
//         handelPriceSelection(finalAmount);
//       }

//       return updatedSelection;
//     });
//   };

//   const getTextClass = (isMember) =>
//     `text-nowrap text-14-300-18 ${isMember ? 'color-c5c5' : 'color-black-olive'
//     }`;


//   const rowData = (
//     selectedData?.length > 0 ? selectedData : addOnServicesData?.services || []
//   ).map(({ id, category, free_member, premium_member }) => {
//     // const convertPrice = (amount) =>
//     //   isNational ? amount : (amount / inrPrice).toFixed(2);

//     const convertPrice = (price) => {
//       let convertedPrice = parseFloat(price);
//       if (isNaN(convertedPrice)) return 0;
//       if (pricing_currency === "USD" && isNational) {
//         // USD → INR
//         convertedPrice = convertedPrice * inrPrice;
//       } else if (pricing_currency === "INR" && !isNational) {
//         // INR → USD
//         convertedPrice = convertedPrice / inrPrice;
//       }

//       return Number(convertedPrice.toFixed(2));
//     };

//     return {
//       data: [
//         {
//           value: (
//             <div className="d-flex cms-10 ">
//               <input
//                 type="checkbox"
//                 className={`custom-checkbox ${selectedIds.includes(id) ? 'activeChecked' : 'activeUnchecked'
//                   }`}
//                 onChange={(e) => handleCheckboxChange(id, e.target.checked)}
//                 checked={selectedIds.includes(id)}
//               />
//               <span className="text-center w-100">{category}</span>
//             </div>
//           ),
//         },
//         {
//           value: (
//             <div className={getTextClass(isPremiumMember)}>
//               {`${['0', '1', '6'].includes(user_type) ? pricing_currency === 'INR' ? '₹' : '$' : isNational ? '₹' : '$'} ${['0', '1', '6'].includes(user_type) ? free_member : convertPrice(free_member)}`}
//             </div>
//           ),
//         },
//         {
//           value: (
//             <div className={getTextClass(isFreeMember)}>
//               {`${['0', '1', '6'].includes(user_type) ? pricing_currency === 'INR' ? '₹' : '$' : isNational ? '₹' : '$'} ${['0', '1', '6'].includes(user_type) ? premium_member : convertPrice(premium_member)}`}
//             </div>
//           ),
//         },
//       ],
//     };
//   });

//   return (
//     <div
//       id="common-registration-block"
//       className="w-100 overflow-auto iferp-scroll"
//     >
//       <EventPricing
        
//         isNational={isNational}
//         priceArray={priceArray}
//         isEdit={isEdit}
//         handelClick={handelClick} />

//       {rowData?.length > 0 && (
//         <>
//           <div className="text-16-500 color-title-navy font-poppins mb-3 cmt-5">
//             Add-On Services
//           </div>
//           <Table
//             header={header}
//             rowData={rowData}
//             customHeaderStyle={{
//               backgroundColor: '#2148C0',
//               color: 'white',
//             }}
//           />
//         </>
//       )}
//       {add_on_pricing.length > 0 && (
//         <>
//           <div className="text-18-500 color-title-navy font-poppins mb-3 pt-3">
//             Add On Pricing
//           </div>
//           <BootstrapTable bordered>
//             <thead className="table-header-container">
//               <tr className="table-header-row-container">
//                 <th className="header-cell">Add On Categories</th>
//                 <th className="header-cell">Premium Members</th>
//                 <th className="header-cell">Non-Premium Members</th>
//               </tr>
//             </thead>
//             <tbody className="table-body-container">
//               {add_on_pricing.map((elem, index) => {
//                 const { id, category, premium_members, non_premium_members } =
//                   elem;
//                                const convertPrice = (price) => {
//                   let convertedPrice = parseFloat(price);
//                   if (isNaN(convertedPrice)) return 0;
//                   if (pricing_currency === "USD" && isNational) {
//                     // USD → INR
//                     convertedPrice = convertedPrice * inrPrice;
//                   } else if (pricing_currency === "INR" && !isNational) {
//                     // INR → USD
//                     convertedPrice = convertedPrice / inrPrice;
//                   }

//                   return Number(convertedPrice.toFixed(2));
//                 };

//                 return (
//                   <tr className="table-body-row-container" key={index}>
//                     <td className="text-16-400 color-black-olive row-cell text-start ps-5 pe-2">
//                       {category}
//                     </td>
//                     <td className="text-16-500 color-black-olive row-cell text-start ps-5 pe-2">
//                       {isEdit ? (
//                         <RadioInput
//                           id={`add_on_pricing-${id}-premium_members`}
//                           checked={addOnPriceArray.includes(
//                             `add_on_pricing-${id}-premium_members`
//                           )}
//                           label={`${['0', '1', '6'].includes(user_type) ? pricing_currency === 'INR' ? '₹' : '$' : isNational ? '₹' : '$'}  ${['0', '1', '6'].includes(user_type) ? premium_members : convertPrice(premium_members)}`}
//                           className="pe-4"
//                           value="1"
//                           onChange={handelClick}
//                         />
//                       ) : (
//                         `${['0', '1', '6'].includes(user_type) ? pricing_currency === 'INR' ? '₹' : '$' : isNational ? '₹' : '$'}  ${['0', '1', '6'].includes(user_type) ? premium_members : convertPrice(premium_members)}`
//                       )}
//                     </td>
//                     <td className="text-16-500 color-black-olive row-cell text-start ps-5 pe-2">
//                       {isEdit ? (
//                         <RadioInput
//                           id={`add_on_pricing-${id}-non_premium_members`}
//                           checked={addOnPriceArray.includes(
//                             `add_on_pricing-${id}-non_premium_members`
//                           )}
//                           label={`${['0', '1', '6'].includes(user_type) ? pricing_currency === 'INR' ? '₹' : '$' : isNational ? '₹' : '$'} ${['0', '1', '6'].includes(user_type) ? non_premium_members : convertPrice(non_premium_members)}`}
//                           className="pe-4"
//                           value="1"
//                           onChange={handelClick}
//                         />
//                       ) : (
//                         `${['0', '1', '6'].includes(user_type) ? pricing_currency === 'INR' ? '₹' : '$' : isNational ? '₹' : '$'} ${['0', '1', '6'].includes(user_type) ? non_premium_members : convertPrice(non_premium_members)}`
//                       )}
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </BootstrapTable>
//         </>
//       )}
//     </div>
//   );
// };
// export default ViewRegistration;





// import React, { useState } from 'react';
// import { useSelector } from 'react-redux';
// import { cloneDeep, forEach } from 'lodash';
// import { Table } from 'react-bootstrap';
// import RadioInput from 'components/form/RadioInput';
// import { getDataFromLocalStorage } from 'utils/helpers';
// import './Registration.scss';

// const ViewRegistration = ({
//   isEdit,
//   handelPriceSelection,
//   isNational,
//   inrPrice,
// }) => {

//   const [priceArray, setPriceArray] = useState([]);
//   const [addOnPriceArray, setAddOnPriceArray] = useState([]);
//   const userData = getDataFromLocalStorage();
//   const { user_type, membership_plan_id } = userData;
//   const { eventData } = useSelector((state) => ({
//     eventData:
//       user_type === '0' ? state.global.eventData : state.global.eventData,
//   }));
//   const {
//     event_pricing = [],
//     add_on_pricing = [],
//     dashboard_pricing_selection = [],
//     pricing_categories = [],
//     event_mode,
//     pricing_currency,
//   } = eventData;
//   const findCategory =
//     user_type === '0'
//       ? pricing_categories
//       : dashboard_pricing_selection.find(
//           (o) => `${o.id}` === `${membership_plan_id}`
//         )?.data || [];

//   const handelClick = (e) => {
//     const elemID = e.target.id;
//     let list = cloneDeep(priceArray);
//     let addonlist = cloneDeep(addOnPriceArray);
//     if (elemID.includes('event_pricing')) {
//       if (list.includes(elemID)) {
//         list = list.filter((o) => o !== elemID);
//       } else {
//         // list = [...list, elemID];
//         list = [elemID];
//       }
//       setPriceArray(list);
//     } else {
//       if (addonlist.includes(elemID)) {
//         addonlist = addonlist.filter((o) => o !== elemID);
//       } else {
//         const idArray = elemID.split('-');

//         addonlist = addonlist.filter(
//           (o) =>
//             o !==
//             `${idArray[0]}-${idArray[1]}-${
//               idArray[2] === 'non_premium_members'
//                 ? 'premium_members'
//                 : 'non_premium_members'
//             }`
//         );
//         addonlist = [...addonlist, elemID];
//       }
//       setAddOnPriceArray(addonlist);
//     }
//     let amount = 0;
//     forEach(list, (elm) => {
//       const splitData = elm.split('-');
//       if (splitData[0] === 'event_pricing') {
//         const parentFind = event_pricing.find(
//           (o) => `${o.id}` === splitData[1]
//         )?.[splitData[2]];
//         let childFind = parentFind.find((o) => `${o.id}` === splitData[3])?.[
//           splitData[4]
//         ];
//         let priceAmount = childFind ? +childFind : 0;
//         amount = amount + priceAmount;
//       }
//     });
//     forEach(addonlist, (elm) => {
//       const splitData = elm.split('-');
//       if (splitData[0] === 'add_on_pricing') {
//         const res = add_on_pricing.find((o) => `${o.id}` === splitData[1])?.[
//           splitData[2]
//         ];
//         amount = amount + +res;
//       }
//     });
//     if (handelPriceSelection) {
//       handelPriceSelection(amount);
//     }
//   };

//   const showEventPricing =
//     findCategory.some((o) => o.is_checked) || user_type === '0';

//   return (
//     <div
//       id="common-registration-block"
//       className="w-100 overflow-auto iferp-scroll"
//     >
//       {event_pricing.length === 0 && (
//         <div className="d-flex justify-content-center text-14-400 cpb-20 cmt-24">
//           No Data Found
//         </div>
//       )}
//       {event_pricing.length > 0 && showEventPricing && (
//         <>
//           <div className="text-18-500 color-title-navy font-poppins mb-3">
//             Event Pricing
//           </div>
//           <Table bordered className="">
//             <thead className="table-header-container">
//               <tr className="table-header-row-container">
//                 <th className="header-cell" rowSpan={2}>
//                   Registration Categories
//                 </th>
//                 <th className="header-cell" rowSpan={2}>
//                   Event Type
//                 </th>
//                 {findCategory.map((elem, index) => {
//                   return (
//                     <th
//                       className={`header-cell ${
//                         elem?.is_checked || user_type === '0' ? '' : 'd-none'
//                       }`}
//                       key={index}
//                       colSpan={3}
//                     >
//                       {elem.category}
//                     </th>
//                   );
//                 })}
//               </tr>
//               <tr className="table-header-row-container">
//                 {findCategory.map((elem, index) => {
//                   return (
//                     <React.Fragment key={index}>
//                       <th
//                         className={`header-cell ${
//                           elem?.is_checked || user_type === '0' ? '' : 'd-none'
//                         }`}
//                       >
//                         Early Bird Price
//                       </th>
//                       <th
//                         className={`header-cell ${
//                           elem?.is_checked || user_type === '0' ? '' : 'd-none'
//                         }`}
//                       >
//                         Final Price
//                       </th>
//                       <th
//                         className={`header-cell ${
//                           elem?.is_checked || user_type === '0' ? '' : 'd-none'
//                         }`}
//                       >
//                         On Spot Price
//                       </th>
//                     </React.Fragment>
//                   );
//                 })}
//               </tr>
//             </thead>
//             <tbody className="table-body-container">
//               {event_pricing.map((elem, index) => {
//                 const { id, physical, virtual } = elem;
//                 return (
//                   <React.Fragment key={index}>
//                     <tr className="table-body-row-container">
//                       <td
//                         className="text-16-400 color-black-olive row-cell ps-2 pe-2 text-start"
//                         // rowSpan={event_mode === "Physical" ? 1 : 2}
//                         // rowSpan={["Physical","Virtual"].includes(event_mode)? 1 : 2}
//                       >
//                         {elem.category}
//                       </td>
//                       {['Hybrid', 'Physical']?.some((mode) =>
//                         event_mode?.split(',')?.includes(mode)
//                       ) && (
//                         <td className="text-16-500 color-black-olive row-cell ps-5 pe-5 text-start">
//                           Physical
//                         </td>
//                       )}
//                       {['Hybrid', 'Physical']?.some((mode) =>
//                         event_mode?.split(',')?.includes(mode)
//                       ) &&
//                         physical?.map((pElem, pIndex) => {
//                           const isShow =
//                             findCategory.find((o) => o.id === pElem.id)
//                               ?.is_checked || user_type === '0';

//                           const convertPrice = (price) => {
//                             if (pricing_currency === 'USD' && isNational)
//                               return (price / inrPrice).toFixed(2);
//                             if (pricing_currency === 'INR' && !isNational)
//                               return (price * inrPrice).toFixed(2);
//                             return price;
//                           };

//                           const newOralPrice = convertPrice(pElem.oral);
//                           const newPosterPrice = convertPrice(pElem.poster);
//                           const newonSpotPrice = convertPrice(pElem.onSpot);

//                           {
//                             /*
//                           const newOralPrice = isNational
//                             ? pElem.oral
//                             : (pElem.oral * inrPrice).toFixed(2);
//                           const newPosterPrice = isNational
//                             ? pElem.poster
//                             : (pElem.poster * inrPrice).toFixed(2);
//                           const newonSpotPrice = isNational
//                             ? pElem.onSpot
//                             : (pElem.onSpot * inrPrice).toFixed(2); */
//                           }

//                           return (
//                             <React.Fragment key={pIndex}>
//                               <td
//                                 className={`row-cell ps-3 pe-2 text-start ${
//                                   isShow ? '' : 'd-none'
//                                 }`}
//                               >
//                                 <div>
//                                   {isEdit ? (
//                                     <RadioInput
//                                       id={`event_pricing-${id}-physical-${pElem.id}-oral`}
//                                       label={`${
//                                         isNational ? '₹' : '$'
//                                       }${newOralPrice}`}
//                                       className="pe-4"
//                                       value="1"
//                                       checked={priceArray.includes(
//                                         `event_pricing-${id}-physical-${pElem.id}-oral`
//                                       )}
//                                       onChange={handelClick}
//                                     />
//                                   ) : (
//                                     `${isNational ? '₹' : '$'}${newOralPrice}`
//                                   )}
//                                 </div>
//                               </td>
//                               <td
//                                 className={`row-cell ps-3 pe-2 text-start ${
//                                   isShow ? '' : 'd-none'
//                                 }`}
//                               >
//                                 <div>
//                                   {isEdit ? (
//                                     <RadioInput
//                                       id={`event_pricing-${id}-physical-${pElem.id}-poster`}
//                                       label={`${
//                                         isNational ? '₹' : '$'
//                                       }${newPosterPrice}`}
//                                       className="pe-4"
//                                       value="1"
//                                       checked={priceArray.includes(
//                                         `event_pricing-${id}-physical-${pElem.id}-poster`
//                                       )}
//                                       onChange={handelClick}
//                                     />
//                                   ) : (
//                                     `${isNational ? '₹' : '$'}${newPosterPrice}`
//                                   )}
//                                 </div>
//                               </td>
//                               <td
//                                 className={`row-cell ps-3 pe-2 text-start ${
//                                   isShow ? '' : 'd-none'
//                                 }`}
//                               >
//                                 <div>
//                                   {isEdit ? (
//                                     <RadioInput
//                                       id={`event_pricing-${id}-physical-${pElem.id}-onSpot`}
//                                       label={`${
//                                         isNational ? '₹' : '$'
//                                       }${newonSpotPrice}`}
//                                       className="pe-4"
//                                       value="1"
//                                       checked={priceArray.includes(
//                                         `event_pricing-${id}-physical-${pElem.id}-onSpot`
//                                       )}
//                                       onChange={handelClick}
//                                     />
//                                   ) : (
//                                     `${isNational ? '₹' : '$'}${newonSpotPrice}`
//                                   )}
//                                 </div>
//                               </td>
//                             </React.Fragment>
//                           );
//                         })}
//                     </tr>
//                     {['Hybrid', 'Virtual'].includes(event_mode) && (
//                       <tr className="table-body-row-container">
//                         <td className="text-16-500 color-black-olive row-cell ps-5 pe-5 text-start">
//                           Virtual
//                         </td>
//                         {virtual?.map((vElem, vIndex) => {
//                           const isShow =
//                             findCategory.find((o) => o.id === vElem.id)
//                               ?.is_checked || user_type === '0';
//                           const newOralPrice = isNational
//                             ? vElem.oral
//                             : (vElem.oral * inrPrice).toFixed(2);
//                           const newPosterPrice = isNational
//                             ? vElem.poster
//                             : (vElem.poster * inrPrice).toFixed(2);
//                           return (
//                             <React.Fragment key={vIndex}>
//                               <td
//                                 className={`row-cell ps-3 pe-2 text-start ${
//                                   isShow ? '' : 'd-none'
//                                 }`}
//                               >
//                                 <div>
//                                   {isEdit ? (
//                                     <RadioInput
//                                       id={`event_pricing-${id}-virtual-${vElem.id}-oral`}
//                                       label={`${
//                                         isNational ? '₹' : '$'
//                                       }${newOralPrice}`}
//                                       className="pe-4"
//                                       value="1"
//                                       checked={priceArray.includes(
//                                         `event_pricing-${id}-virtual-${vElem.id}-oral`
//                                       )}
//                                       onChange={handelClick}
//                                     />
//                                   ) : (
//                                     `${isNational ? '₹' : '$'}${newOralPrice}`
//                                   )}
//                                 </div>
//                               </td>
//                               <td
//                                 className={`row-cell ps-3 pe-2 text-start ${
//                                   isShow ? '' : 'd-none'
//                                 }`}
//                               >
//                                 <div>
//                                   {isEdit ? (
//                                     <RadioInput
//                                       id={`event_pricing-${id}-virtual-${vElem.id}-poster`}
//                                       label={`${
//                                         isNational ? '₹' : '$'
//                                       }${newPosterPrice}`}
//                                       className="pe-4"
//                                       value="1"
//                                       checked={priceArray.includes(
//                                         `event_pricing-${id}-virtual-${vElem.id}-poster`
//                                       )}
//                                       onChange={handelClick}
//                                     />
//                                   ) : (
//                                     `${isNational ? '₹' : '$'}${newPosterPrice}`
//                                   )}
//                                 </div>
//                               </td>
//                             </React.Fragment>
//                           );
//                         })}
//                       </tr>
//                     )}
//                   </React.Fragment>
//                 );
//               })}
//             </tbody>
//           </Table>
//         </>
//       )}
//       {add_on_pricing.length > 0 && (
//         <>
//           <div className="text-18-500 color-title-navy font-poppins mb-3 pt-3">
//             Add On Pricing
//           </div>
//           <Table bordered>
//             <thead className="table-header-container">
//               <tr className="table-header-row-container">
//                 <th className="header-cell">Add On Categories</th>
//                 <th className="header-cell">Premium Members</th>
//                 <th className="header-cell">Non-Premium Members</th>
//               </tr>
//             </thead>
//             <tbody className="table-body-container">
//               {add_on_pricing.map((elem, index) => {
//                 const { id, category, premium_members, non_premium_members } =
//                   elem;
//                 const newPremiumPrice = isNational
//                   ? premium_members
//                   : (premium_members * inrPrice).toFixed(2);
//                 const newNonPremiumPrice = isNational
//                   ? non_premium_members
//                   : (non_premium_members * inrPrice).toFixed(2);
//                 return (
//                   <tr className="table-body-row-container" key={index}>
//                     <td className="text-16-400 color-black-olive row-cell text-start ps-5 pe-2">
//                       {category}
//                     </td>
//                     <td className="text-16-500 color-black-olive row-cell text-start ps-5 pe-2">
//                       {isEdit ? (
//                         <RadioInput
//                           id={`add_on_pricing-${id}-premium_members`}
//                           checked={addOnPriceArray.includes(
//                             `add_on_pricing-${id}-premium_members`
//                           )}
//                           label={`${isNational ? '₹' : '$'}${newPremiumPrice}`}
//                           className="pe-4"
//                           value="1"
//                           onChange={handelClick}
//                         />
//                       ) : (
//                         `${isNational ? '₹' : '$'}${newPremiumPrice}`
//                       )}
//                     </td>
//                     <td className="text-16-500 color-black-olive row-cell text-start ps-5 pe-2">
//                       {isEdit ? (
//                         <RadioInput
//                           id={`add_on_pricing-${id}-non_premium_members`}
//                           checked={addOnPriceArray.includes(
//                             `add_on_pricing-${id}-non_premium_members`
//                           )}
//                           label={`${
//                             isNational ? '₹' : '$'
//                           }${newNonPremiumPrice}`}
//                           className="pe-4"
//                           value="1"
//                           onChange={handelClick}
//                         />
//                       ) : (
//                         `${isNational ? '₹' : '$'}${newNonPremiumPrice}`
//                       )}
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </Table>
//         </>
//       )}
//     </div>
//   );
// };
// export default ViewRegistration;
